import { plainHeadingText } from '../fmHostedHeading';
import {
  assertArray,
  assertFiniteNumber,
  assertNonEmptyString,
  assertRecord,
  hasOwn,
  rejectUnknownKeys,
  validateEnum,
  validateOptionalCallback,
  validateOptionalFiniteNumber,
  validateOptionalString,
  validateTone,
} from './elementFields.validation';

function validateChartSeries(value: unknown, label: string): void {
  assertArray(value, label);
  value.forEach((series, index) => {
    const itemLabel = `${label}[${index}]`;
    assertRecord(series, itemLabel);
    rejectUnknownKeys(series, ['code', 'label', 'tone', 'dataKey', 'stackKey', 'role'], itemLabel);
    if (typeof series.code !== 'string' && typeof series.code !== 'number') {
      throw new Error(`Invalid descriptor: ${itemLabel}.code must be a string or number.`);
    }
    assertNonEmptyString(series.label, `${itemLabel}.label`);
    validateTone(series, itemLabel);
    validateOptionalString(series, 'dataKey', itemLabel);
    validateOptionalString(series, 'stackKey', itemLabel);
    validateEnum(series.role, ['reference', 'result'], `${itemLabel}.role`, true);
  });
}

function validateDiameterDistributions(value: unknown, label: string): void {
  assertArray(value, label);
  value.forEach((distribution, index) => {
    const itemLabel = `${label}[${index}]`;
    assertRecord(distribution, itemLabel);
    rejectUnknownKeys(distribution, ['speciesCode', 'label', 'bins'], itemLabel);
    if (!Number.isInteger(distribution.speciesCode)) {
      throw new Error(`Invalid descriptor: ${itemLabel}.speciesCode must be a species code.`);
    }
    assertNonEmptyString(distribution.label, `${itemLabel}.label`);
    assertArray(distribution.bins, `${itemLabel}.bins`);
    distribution.bins.forEach((bin, binIndex) => {
      const binLabel = `${itemLabel}.bins[${binIndex}]`;
      assertRecord(bin, binLabel);
      rejectUnknownKeys(bin, ['diameter_cm', 'n_per_ha'], binLabel);
      ['diameter_cm', 'n_per_ha'].forEach((key) => {
        if (typeof bin[key] !== 'number' || !Number.isFinite(bin[key])) {
          throw new Error(`Invalid descriptor: ${binLabel}.${key} must be a finite number.`);
        }
      });
    });
  });
}

function validateChartDataBase(value: Record<string, unknown>, label: string): void {
  assertArray(value.data, `${label}.data`);
  value.data.forEach((row, index) => {
    const rowLabel = `${label}.data[${index}]`;
    assertRecord(row, rowLabel);
    Object.entries(row).forEach(([key, entry]) => {
      if (
        !key.trim() ||
        (entry !== null &&
          entry !== undefined &&
          typeof entry !== 'string' &&
          typeof entry !== 'number')
      ) {
        throw new Error(`Invalid descriptor: ${rowLabel}.${key} must be chart data.`);
      }
    });
  });
  assertNonEmptyString(value.xKey, `${label}.xKey`);
}

function validateGrowthChart(value: Record<string, unknown>, label: string): void {
  if (hasOwn(value, 'series') === hasOwn(value, 'valueKey')) {
    throw new Error(`Invalid descriptor: ${label} requires exactly one growth value source.`);
  }
  if (hasOwn(value, 'series')) validateChartSeries(value.series, `${label}.series`);
  else {
    assertNonEmptyString(value.valueKey, `${label}.valueKey`);
    assertNonEmptyString(value.valueLabel, `${label}.valueLabel`);
  }
  validateEnum(value.render, ['line', 'area', 'stacked-area'], `${label}.render`, true);
  if (!['volume', 'height', 'diameter', 'basalArea', 'stems'].includes(String(value.metric))) {
    throw new Error(`Invalid descriptor: ${label}.metric is not registered.`);
  }
}

function validateShareDonutChart(
  value: Record<string, unknown>,
  label: string,
  commonDataKeys: readonly string[],
): void {
  rejectUnknownKeys(
    value,
    [...commonDataKeys, 'valueKey', 'toneKey', 'colorMode', 'speciesCodeKey'],
    label,
  );
  validateChartDataBase(value, label);
  assertNonEmptyString(value.valueKey, `${label}.valueKey`);
  validateOptionalString(value, 'toneKey', label);
  validateEnum(value.colorMode, ['tone', 'species'], `${label}.colorMode`, true);
  validateOptionalString(value, 'speciesCodeKey', label);
  if (value.colorMode === 'species' && value.speciesCodeKey === undefined) {
    throw new Error(
      `Invalid descriptor: ${label}.speciesCodeKey is required for species color mode.`,
    );
  }
  if (value.colorMode !== 'species' && value.speciesCodeKey !== undefined) {
    throw new Error(`Invalid descriptor: ${label}.speciesCodeKey requires species color mode.`);
  }
}

/** Species series colour by code and assortment series by role; names are refused. */
function validateSeriesCodesForMode(series: unknown, mode: string, label: string): void {
  assertArray(series, label);
  series.forEach((entry, index) => {
    assertRecord(entry, `${label}[${index}]`);
    const valid =
      mode === 'species'
        ? Number.isInteger(entry.code)
        : ['sawlog', 'pulpwood', 'energy'].includes(String(entry.code));
    if (!valid) {
      throw new Error(
        `Invalid descriptor: ${label}[${index}].code "${String(entry.code)}" is not a ${mode === 'species' ? 'species code' : 'assortment key'}.`,
      );
    }
  });
}

function validateCashflowStoryChart(value: Record<string, unknown>, label: string): void {
  rejectUnknownKeys(
    value,
    ['kind', 'xAxisLabel', 'valueLabel', 'presentation', 'seriesLabels', 'periods'],
    label,
  );
  assertNonEmptyString(value.xAxisLabel, `${label}.xAxisLabel`);
  assertNonEmptyString(value.valueLabel, `${label}.valueLabel`);
  validateEnum(
    value.presentation,
    ['signed-net-bars', 'grouped-bars-net-line'],
    `${label}.presentation`,
  );
  assertRecord(value.seriesLabels, `${label}.seriesLabels`);
  const seriesLabels = value.seriesLabels;
  rejectUnknownKeys(seriesLabels, ['income', 'cost', 'net'], `${label}.seriesLabels`);
  ['income', 'cost', 'net'].forEach((key) =>
    assertNonEmptyString(seriesLabels[key], `${label}.seriesLabels.${key}`),
  );
  assertArray(value.periods, `${label}.periods`);
  value.periods.forEach((period, index) => {
    const periodLabel = `${label}.periods[${index}]`;
    assertRecord(period, periodLabel);
    rejectUnknownKeys(period, ['period', 'income', 'cost', 'net'], periodLabel);
    assertNonEmptyString(period.period, `${periodLabel}.period`);
    ['income', 'cost', 'net'].forEach((key) =>
      validateOptionalFiniteNumber(period, key, periodLabel),
    );
  });
}

const CORRIDOR_VERDICTS = [
  'inside',
  'below-band',
  'above-band',
  'legal-risk',
  'uncovered',
  'no-residual',
] as const;

function validateCorridorRowsStoryChart(value: Record<string, unknown>, label: string): void {
  rejectUnknownKeys(value, ['kind', 'rows', 'bandLabel', 'floorLabel', 'residualLabel'], label);
  ['bandLabel', 'floorLabel', 'residualLabel'].forEach((key) =>
    assertNonEmptyString(value[key], `${label}.${key}`),
  );
  assertArray(value.rows, `${label}.rows`);
  value.rows.forEach((row, index) => {
    const rowLabel = `${label}.rows[${index}]`;
    assertRecord(row, rowLabel);
    rejectUnknownKeys(
      row,
      [
        'id',
        'label',
        'residual',
        'bandLo',
        'bandHi',
        'legalMin',
        'verdict',
        'verdictLabel',
        'detail',
      ],
      rowLabel,
    );
    ['id', 'label', 'verdictLabel', 'detail'].forEach((key) =>
      assertNonEmptyString(row[key], `${rowLabel}.${key}`),
    );
    validateEnum(row.verdict, CORRIDOR_VERDICTS, `${rowLabel}.verdict`);
    ['residual', 'bandLo', 'bandHi', 'legalMin'].forEach((key) => {
      if (row[key] !== null) assertFiniteNumber(row[key], `${rowLabel}.${key}`);
    });
  });
}

/** Charts whose payload is plotted rows rather than a served data table. */
const PLOT_CHART_VALIDATORS = new Map<
  string,
  (value: Record<string, unknown>, label: string) => void
>([
  ['cashflow', validateCashflowStoryChart],
  ['corridor-rows', validateCorridorRowsStoryChart],
]);

const CARTESIAN_STORY_CHARTS = new Set([
  'species-share',
  'ranked-bar',
  'value-growth',
  'growth-trajectory',
]);

function validateDiameterDistributionChart(value: Record<string, unknown>, label: string): void {
  rejectUnknownKeys(
    value,
    [
      'kind',
      'xAxisLabel',
      'distributions',
      'removedDistributions',
      'cutLabel',
      'keptLabel',
    ],
    label,
  );
  assertNonEmptyString(value.xAxisLabel, `${label}.xAxisLabel`);
  if (value.removedDistributions !== undefined)
    assertNonEmptyString(value.cutLabel, `${label}.cutLabel`);
  if (value.keptLabel !== undefined) {
    if (value.removedDistributions === undefined) {
      throw new Error(
        `Invalid descriptor: ${label}.keptLabel needs ${label}.removedDistributions.`,
      );
    }
    assertNonEmptyString(value.keptLabel, `${label}.keptLabel`);
  }
  validateDiameterDistributions(value.distributions, `${label}.distributions`);
  if (value.removedDistributions !== undefined)
    validateDiameterDistributions(
      value.removedDistributions,
      `${label}.removedDistributions`,
    );
}

export function validateChart(value: unknown, label: string): void {
  assertRecord(value, label);
  assertNonEmptyString(value.kind, `${label}.kind`);
  const plotValidator = PLOT_CHART_VALIDATORS.get(value.kind);
  if (plotValidator) {
    plotValidator(value, label);
    return;
  }
  const commonDataKeys = ['kind', 'data', 'xKey', 'activeKey'];
  const cartesianKeys = [...commonDataKeys, 'xAxisLabel'];
  if (CARTESIAN_STORY_CHARTS.has(String(value.kind))) {
    assertNonEmptyString(value.xAxisLabel, `${label}.xAxisLabel`);
  }
  switch (value.kind) {
    case 'species-share':
      rejectUnknownKeys(value, [...cartesianKeys, 'series', 'mode'], label);
      validateChartDataBase(value, label);
      validateChartSeries(value.series, `${label}.series`);
      if (value.mode !== 'species' && value.mode !== 'assortment') {
        throw new Error(`Invalid descriptor: ${label}.mode must be species or assortment.`);
      }
      validateSeriesCodesForMode(value.series, value.mode, `${label}.series`);
      break;
    case 'ranked-bar':
    case 'value-growth':
      rejectUnknownKeys(value, [...cartesianKeys, 'valueKey', 'valueLabel', 'reference'], label);
      validateChartDataBase(value, label);
      assertNonEmptyString(value.valueKey, `${label}.valueKey`);
      assertNonEmptyString(value.valueLabel, `${label}.valueLabel`);
      if (value.reference !== undefined) {
        assertRecord(value.reference, `${label}.reference`);
        rejectUnknownKeys(value.reference, ['valueKey', 'valueLabel'], `${label}.reference`);
        assertNonEmptyString(value.reference.valueKey, `${label}.reference.valueKey`);
        assertNonEmptyString(value.reference.valueLabel, `${label}.reference.valueLabel`);
        if (value.reference.valueKey === value.valueKey) {
          throw new Error(
            `Invalid descriptor: ${label}.reference.valueKey must name a second value, not "${value.valueKey}" again.`,
          );
        }
      }
      break;
    case 'share-donut':
      validateShareDonutChart(value, label, commonDataKeys);
      break;
    case 'growth-trajectory':
      rejectUnknownKeys(
        value,
        [...cartesianKeys, 'series', 'valueKey', 'valueLabel', 'render', 'metric'],
        label,
      );
      validateChartDataBase(value, label);
      validateGrowthChart(value, label);
      break;
    case 'time-series':
      rejectUnknownKeys(value, [...commonDataKeys, 'series', 'legend', 'onRangeChange'], label);
      validateChartDataBase(value, label);
      validateChartSeries(value.series, `${label}.series`);
      assertArray(value.series, `${label}.series`);
      if (value.series.length === 0) {
        throw new Error(`Invalid descriptor: ${label}.series must not be empty.`);
      }
      validateEnum(value.legend, ['auto', 'none'], `${label}.legend`, true);
      validateOptionalCallback(value, 'onRangeChange', label);
      break;
    case 'diameter-distribution':
      validateDiameterDistributionChart(value, label);
      break;
    default:
      throw new Error(`Invalid descriptor: unregistered chart kind "${String(value.kind)}".`);
  }
}

export const CHART_CHROME_KEYS = [
  'locale',
  'title',
  'caption',
  'unit',
  'takeaway',
  'explanationTitle',
  'contextDetail',
  'source',
  'valueKind',
  'emptyLabel',
  'testId',
] as const;

/** Chart chrome lives outside the plotted data, so an unavailable chart keeps it. */
export function validateChartChrome(value: Record<string, unknown>, label: string): void {
  ['title', 'caption', 'unit', 'takeaway'].forEach((key) =>
    assertNonEmptyString(value[key], `${label}.${key}`),
  );
  // The title prints above the plot and the caption below it; equal strings
  // say the same sentence twice around one figure.
  if (plainHeadingText(String(value.caption)) === plainHeadingText(String(value.title))) {
    throw new Error(
      `Invalid descriptor: ${label}.caption repeats ${label}.title ("${String(value.title)}"); the caption must say what the title does not.`,
    );
  }
  ['locale', 'explanationTitle', 'contextDetail', 'source', 'emptyLabel', 'testId'].forEach((key) =>
    validateOptionalString(value, key, label),
  );
  validateEnum(
    value.valueKind,
    ['currency', 'percent', 'number', 'volume', 'area', 'date', 'text', 'unavailable'],
    `${label}.valueKind`,
    true,
  );
}
